import { ChronoIcon } from "@images/svgIcons/chronoIcon"
import { CommentIcon } from "@images/svgIcons/commentIcon"
import { ExerciseDoneCheckIcon } from "@images/svgIcons/exerciseDoneCheckIcon"
import { MarkAsDoneIcon } from "@images/svgIcons/markAsDoneIcon"
import { ThrophIcon } from "@images/svgIcons/throphIcon"
import { displayTotalTime, getTrainingChronoTitle } from "@modules/chrono/chronoUtils"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { ScoreType } from "@modules/scoreboard/scoreTypes"
import { getScoreTypesArray } from "@modules/scoreboard/scoreUtils"
import { RpeTag } from "@modules/sessionBlock/exercises/components/rpeTag"
import { Exercise } from "@modules/sessionBlock/exercises/exerciseTypes"
import { getDisplayExerciseCategory } from "@modules/sessionBlock/exercises/exerciseUtils"
import { useScoreCommentFormSidebar } from "@modules/sessionBlock/exercises/hooks/useScoreCommentFormSidebar"
import { ParentSessionOrigin } from "@modules/sessionBlock/exercises/sessionTypes"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { ThemeMode } from "@modules/ui/styles/theme"
import { useGetDisplayedScore } from "@modules/utils/hooks/useUnit"
import { LoggerType } from "@modules/utils/loggerUtils"
import { Tag } from "primereact/tag"
import { useMemo, useState } from "react"
import styled, { useTheme } from "styled-components"

type ExerciseDetailProps = {
  exercise: Exercise
  parentSessionOrigin: ParentSessionOrigin
  canUserCompleteExercise?: boolean
  isCoach: boolean
  refreshSession?: () => void
}

export const ExerciseDetail: React.FC<ExerciseDetailProps> = ({
  exercise,
  parentSessionOrigin,
  canUserCompleteExercise = false,
  isCoach,
  refreshSession,
}) => {
  const [isCompleteLoading, setIsCompleteLoading] = useState(false)
  const [isCompleted, setIsCompleted] = useState(exercise.isCompleted)

  const theme = useTheme()
  const toast = useToast()
  const { sessionService } = useServicesContext()
  const scoreTypes = useMemo(() => getScoreTypesArray(), [])
  const getScore = useGetDisplayedScore()
  const { navigateToCommentForm, navigateToScoreForm } = useScoreCommentFormSidebar(
    exercise,
    parentSessionOrigin,
    refreshSession,
  )

  const displayScoreType = useMemo(() => {
    const scoreTypeLabel = scoreTypes.find((scoreLabelType) => exercise.scoreType === scoreLabelType.value)
    return scoreTypeLabel?.label ?? ""
  }, [exercise])

  const onComplete = async () => {
    if (!isCompleteLoading && !isCompleted) {
      setIsCompleteLoading(true)
      try {
        await sessionService.markExerciseAsComplete(parentSessionOrigin, exercise)
        setIsCompleted(true)
        refreshSession && refreshSession()
      } catch (e) {
        console.error(LoggerType.Exercise, "complete", e)
        toast.show({ severity: "error", summary: word("global.error.label") })
      } finally {
        setIsCompleteLoading(false)
      }
    }
  }

  const renderButton = useMemo(() => {
    if (canUserCompleteExercise) {
      if (isCompleted) {
        return (
          <div className="flex flex-row w-full gap-3">
            {exercise.scoreType !== ScoreType.NO_SCORE ? (
              <HUButton
                text={
                  exercise.score?.value
                    ? word("exercise.card.button.modify_score")
                    : word("exercise.card.button.add_score")
                }
                onClick={() => navigateToScoreForm()}
                type="Left_Icon"
                size="M"
                colorType={exercise.score?.value ? "Tertiary" : "Primary"}
                style={{ padding: "10px 20px" }}
                className="w-full mt-2"
                icon={{ iconView: <ThrophIcon color={theme.secondaryColor} /> }}
              />
            ) : (
              <HUButton
                text={
                  exercise.score?.note || exercise.score?.medias?.length
                    ? word("exercise.card.button.modify_comment")
                    : word("exercise.card.button.add_comment")
                }
                onClick={() => navigateToCommentForm()}
                type="Left_Icon"
                size="M"
                colorType={exercise.score?.note || exercise.score?.medias?.length ? "Tertiary" : "Primary"}
                style={{ padding: "10px 20px" }}
                className="w-full mt-2"
                icon={{ iconView: <CommentIcon color={theme.secondaryColor} height={24} /> }}
              />
            )}
          </div>
        )
      } else {
        return (
          <HUButton
            text={word("exercise.card.button.mark_as_done")}
            onClick={onComplete}
            type="Left_Icon"
            size="M"
            colorType="Primary"
            className="w-full mt-2"
            icon={{ iconView: <MarkAsDoneIcon color={theme.secondaryColor} height={24} /> }}
          />
        )
      }
    }
  }, [isCompleted, canUserCompleteExercise, exercise.score])

  return (
    <div className={"flex flex-column card p-3 gap-3"}>
      {/* Exercise card header */}
      <div
        className={`flex align-items-center gap-2 border-0 py-2 px-3 rounded-33 align-self-start ${theme.mode === ThemeMode.ATHLETE ? "bg-neutralColor-0" : "bg-neutralColor-200"}`}
        style={{ height: 34 }}
      >
        {isCompleted && <ExerciseDoneCheckIcon color={theme.primaryColor700} className="mb-3" />}
        <div className="flex flex-column">
          <HUText fontStyle="TS" color={theme.neutralColor700}>
            {getDisplayExerciseCategory(exercise.category, exercise.otherCategory)}
          </HUText>
        </div>
      </div>

      {/* Exercise card content */}
      <div className="flex flex-column pb-3 border-bottom-1 border-300">
        <div className="flex align-items-center justify-content-between mb-2">
          <div className="flex align-items-center">
            <ChronoIcon color={theme.buttonPrimaryTextColor} />
            <HUText className="ml-2" fontStyle="LM">
              {exercise.chrono?.type
                ? getTrainingChronoTitle(exercise.chrono.type) + " - " + displayTotalTime(exercise.chrono)
                : word("chrono.title.no_chrono")}
            </HUText>
          </div>

          {exercise.rpe !== null && exercise.rpe !== undefined && <RpeTag exercise={exercise} />}
        </div>

        <HURichTextView value={formatTextToRichText(exercise.description)} paragrapheFontStyle="BM" />

        {exercise.score && !!exercise.score.value && exercise.score.value !== 0 && (
          <div className="flex align-items-center mt-4 mb-2">
            <HUText fontStyle="LL">{word("program.session.score")}</HUText>
            <Separator />
            <HUText fontStyle="LL" className="mr-2">
              {getScore(exercise.score.value, exercise.scoreType)}
            </HUText>
            <LevelTag>{exercise.score.difficulty}</LevelTag>
          </div>
        )}

        {exercise.scoreType && exercise.scoreType !== ScoreType.NO_SCORE && !exercise.score?.value && (
          <div className="flex align-items-center mt-4 mb-2">
            <HUText fontStyle="LL">{word("program.session.score")}</HUText>

            <Separator />

            <HUText fontStyle="LL" className="mr-2">
              {displayScoreType}
            </HUText>
          </div>
        )}
      </div>

      {exercise.requiredMedia && (
        <div className="flex flex-row align-items-center justify-content-start my-2 gap-2">
          <span className="pi pi-video" style={{ color: theme.textWarning }} />
          <HUText fontStyle="BS">
            {isCoach
              ? word("program.planning.create.exercise.media_requirement.required")
              : word("program.planning.create.exercise.media_requirement.requested")}
          </HUText>
        </div>
      )}

      {/* Exercise card footer*/}
      <div className="flex flex-row align-items-center justify-content-between">{renderButton}</div>
    </div>
  )
}

const Separator = styled.div`
  display: flex;
  width: 115px;
  height: 1px;
  background-color: ${({ theme }) => theme.neutralColor200};
  margin-left: 15px;
  margin-right: 15px;
`
const LevelTag = styled(Tag)`
  background-color: ${({ theme }) => theme.neutralColor200};
  padding: 4px 9px;
  color: ${({ theme }) => theme.neutralColor700};
  border-radius: 40px;
  font-style: italic;
`
